import { createIntl, createIntlCache } from 'react-intl'
import messages from './index'
import AuthStore from '../stores/AuthStore'
import { STUDENT, TUTOR } from '../stores/UsersStore'
import moment from 'moment'
import 'moment/locale/es'
import 'moment/locale/pt-br'

// Cria o cache para evitar memory leak
const cache = createIntlCache()

// Limpa o cache atual
const clearCache = () => {
  Object.keys(cache).forEach(key => {
    delete cache[key]
  })
}

// Verifica se o usuário é SOMENTE tutor OU SOMENTE student
const isEligibleUser = () => {
  return AuthStore && AuthStore.roles && (
    (AuthStore.roles.includes(TUTOR) && AuthStore.roles.length === 1) ||
    (AuthStore.roles.includes(STUDENT) && AuthStore.roles.length === 1) ||
    (AuthStore.roles.includes(TUTOR) && AuthStore.roles.includes(STUDENT) && AuthStore.roles.length === 2)
  )
}

// Restaura para o idioma padrão
const resetToDefaultLocale = () => {
  sessionStorage.removeItem('userLocale')
  localStorage.removeItem('userLocale')

  // Força a atualização do intl E moment com o idioma padrão
  updateIntl()
}

// Obtém o idioma do usuário atual
const getLocale = () => {
  // Verifica se o AuthStore está disponível
  if (!AuthStore) {
    return 'pt-BR'
  }

  // Se o usuário não estiver autenticado ou não for elegível, sempre usa pt-BR
  if (AuthStore.is_authenticated === false || AuthStore.is_authenticated === undefined || !isEligibleUser()) {
    return 'pt-BR'
  }

  // Tenta obter da sessão atual
  const sessionLocale = sessionStorage.getItem('userLocale')
  const localLocale = localStorage.getItem('userLocale')

  // Se não encontrar nada, retorna o padrão
  return sessionLocale || localLocale || 'pt-BR'
}

// Cria o objeto intl
const createIntlObject = (locale) => {
  // Adiciona timestamp para evitar cache
  const timestamp = Date.now()
  const messagesWithTimestamp = {}

  // Adiciona timestamp em todas as URLs de JSON
  Object.keys(messages[locale] || {}).forEach(key => {
    if (typeof messages[locale][key] === 'string' && messages[locale][key].endsWith('.json')) {
      messagesWithTimestamp[key] = `${messages[locale][key]}?t=${timestamp}`
    } else {
      messagesWithTimestamp[key] = messages[locale][key]
    }
  })

  return createIntl(
    {
      locale: locale,
      defaultLocale: 'pt-BR',
      messages: messagesWithTimestamp
    },
    cache
  )
}

// Cria o objeto inicial
export let intl = createIntlObject(getLocale())

// Atualiza o objeto intl E o locale do moment
export const updateIntl = () => {
  clearCache() // Limpa o cache antes de criar um novo objeto intl
  const currentLocale = getLocale() // Obtém 'en', 'es', ou 'pt-BR'

  // Mapeia o locale da aplicação para o locale do moment ('en', 'es', 'pt-br')
  let momentLocale = currentLocale
  if (currentLocale === 'pt-BR') {
    momentLocale = 'pt-br' // moment usa 'pt-br'
  } else if (currentLocale === 'en') {
    momentLocale = 'en' // moment usa 'en'
  } else if (currentLocale === 'es') {
    momentLocale = 'es' // moment usa 'es'
  }

  // Define o locale global do moment
  moment.locale(momentLocale)

  // Atualiza o objeto intl
  intl = createIntlObject(currentLocale)
}

// Adiciona listeners para atualizar o intl e moment
if (typeof window !== 'undefined') {
  window.addEventListener('localeChanged', updateIntl)

  // Adiciona um listener para o evento authStateChanged
  window.addEventListener('authStateChanged', () => {
    // Se o usuário não for elegível, reseta para o idioma padrão
    if (!isEligibleUser()) {
      // resetToDefaultLocale já chama updateIntl
      resetToDefaultLocale()
    } else {
      updateIntl() // Garante atualização se o usuário se tornar elegível
    }
  })

  // Adiciona um listener para o evento profileChanged
  window.addEventListener('profileChanged', () => {
    // Se o usuário não for elegível, reseta para o idioma padrão
    if (!isEligibleUser()) {
      // resetToDefaultLocale já chama updateIntl
      resetToDefaultLocale()
    } else {
      updateIntl() // Garante atualização se o perfil mudar
    }
  })

  // Chama updateIntl uma vez na inicialização para garantir que moment.locale esteja correto
  updateIntl()
}

// Função compatível com o sistema atual de idiomas
export const setLocale = (newLocale) => {
  // Só permite mudar o idioma se o usuário for elegível
  if (isEligibleUser()) {
    sessionStorage.setItem('userLocale', newLocale)
    localStorage.setItem('userLocale', newLocale)

    // Dispara o evento para que o listener chame updateIntl (que agora atualiza intl e moment)
    window.dispatchEvent(new Event('localeChanged'))
  }
}
